/**
 * /venues/:venueSlug/hall-of-fame — the records board, public.
 *
 * Renders inside AppShell. Two calls in sequence, the same shape as
 * VenueLanding and PublicStandings: by-slug resolves the numeric id, then the
 * records read off it.
 *
 * SECTIONS is the display order and the naming. The endpoint groups by
 * record_type without knowing what any of them are called, and record_type is
 * free text in the schema — so anything it returns that is not named here
 * still renders, under a humanised heading, rather than disappearing. A new
 * record type is then a display decision, not a bug.
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiGet } from '../lib/api.js';
import Icon from '../components/Icon.jsx';

/** Whole numbers print bare; hall_of_fame.record_value is REAL. */
const num = (v) => (Number.isInteger(v) ? String(v) : String(Number(v.toFixed(2))));

const SECTIONS = [
  {
    type:  'most_180s_season',
    title: '180 Kings',
    note:  'The most 180s thrown in a season.',
    value: (v) => `${num(v)} × 180`,
  },
  {
    type:  'highest_checkout_season',
    title: 'Big Fish',
    note:  'The highest checkout of the season.',
    value: (v) => `${num(v)} checkout`,
  },
];

/** 'highest_checkout_alltime' -> 'Highest checkout alltime'. */
function humanise(type) {
  const words = type.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function RecordRow({ record, format, isCurrent }) {
  return (
    <div className="hd-hof-row">
      <div className="hd-hof-season">{record.seasonName ?? 'All time'}</div>
      <div className="hd-hof-name">{record.userName}</div>
      <div className={`hd-hof-value${isCurrent ? ' hd-hof-value-current' : ''}`}>
        {isCurrent && (
          <Icon name="trophy" size={14} className="hd-hof-trophy" />
        )}
        {format(record.recordValue)}
      </div>
    </div>
  );
}

function Section({ title, note, rows, format }) {
  return (
    <section className="hd-hof-section">
      <h2 className="hd-section-title">{title}</h2>
      {note && (
        <p className="hd-meta" style={{ marginTop: -8, marginBottom: 8 }}>{note}</p>
      )}

      {rows.length === 0
        ? <p className="hd-muted">No records yet.</p>
        : rows.map((record, index) => (
            // Rows arrive most-recent-first, so index 0 is the standing holder.
            <RecordRow
              key={record.id}
              record={record}
              format={format}
              isCurrent={index === 0}
            />
          ))}
    </section>
  );
}

export default function HallOfFame() {
  const { venueSlug } = useParams();
  const [state, setState] = useState({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });

    apiGet(`/api/venues/by-slug/${venueSlug}`).then(async ({ data, error }) => {
      if (cancelled) return;

      if (error || !data?.venue) {
        setState({ status: 'error', error: error ?? 'Venue not found' });
        return;
      }

      const board = await apiGet(`/api/venues/${data.venue.id}/public-hall-of-fame`);
      if (cancelled) return;

      if (board.error) {
        setState({ status: 'error', error: board.error, venue: data.venue });
        return;
      }

      setState({ status: 'ready', venue: data.venue, records: board.data.records });
    });

    return () => { cancelled = true; };
  }, [venueSlug]);

  if (state.status === 'loading') {
    return <div className="hd-empty">Loading…</div>;
  }

  if (state.status === 'error') {
    return (
      <div className="hd-stack">
        <h1 className="hd-page-title">
          {state.venue ? 'Hall of fame unavailable' : 'Venue not found'}
        </h1>
        <p className="hd-muted">
          {state.venue ? state.error : <>No venue at <code>/{venueSlug}</code>.</>}
          {' '}
          <Link to={state.venue ? `/venues/${venueSlug}` : '/'}>
            {state.venue ? 'Back to the venue' : 'Back to the home page'}
          </Link>
        </p>
      </div>
    );
  }

  const { venue, records } = state;

  // Any type the endpoint returned that SECTIONS does not name. Rendered rather
  // than dropped — see the note at the top.
  const named = new Set(SECTIONS.map((s) => s.type));
  const extras = Object.keys(records)
    .filter((type) => !named.has(type) && records[type].length > 0)
    .sort();

  const total = Object.values(records).reduce((n, rows) => n + rows.length, 0);

  return (
    <div className="hd-stack">
      <div>
        <h1 className="hd-page-title">Hall of fame</h1>
        <p className="hd-meta">
          <Link to={`/venues/${venue.slug}`}>{venue.name}</Link>
        </p>
      </div>

      {total === 0 ? (
        <div className="hd-empty">
          No records yet — records are set as seasons are played.
        </div>
      ) : (
        <>
          {SECTIONS.map((section) => (
            <Section
              key={section.type}
              title={section.title}
              note={section.note}
              rows={records[section.type] ?? []}
              format={section.value}
            />
          ))}

          {extras.map((type) => (
            <Section
              key={type}
              title={humanise(type)}
              rows={records[type]}
              format={num}
            />
          ))}
        </>
      )}

      <section className="hd-hof-section">
        <h2 className="hd-section-title">Champions</h2>
        <p className="hd-muted">Champions are recorded at season completion.</p>
      </section>
    </div>
  );
}
